import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toastr } from "react-redux-toastr";
import { API_URL } from "../../utils/constant";
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
// import Pagination from "../Pagination";
import Pagination from "../Pagination/Pagination";


// Description component with show more/less functionality
const ExpandableDescription = ({ text, maxLength = 150, username, password, url, secrets }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text && !username && !password && !url && !secrets) return null;

  // Build the full description including credentials
  let fullDescription = text || '';

  // Only show credentials section if there are actual credentials (username/password)
  if (username || password) {
    if (fullDescription) fullDescription += '\n\n--- Credentials ---\n';

    if (username) {
      fullDescription += `Username: ${username}`;
    }
    if (password) {
      fullDescription += fullDescription ? `\nPassword: ${password}` : `Password: ${password}`;
    }
  }

  // Add secrets section if secrets exist
  if (secrets && Object.keys(secrets).length > 0) {
    if (fullDescription) fullDescription += '\n\n--- Exposed Secrets ---\n';

    Object.entries(secrets).forEach(([key, value], index) => {
      if (index > 0) fullDescription += '\n';
      fullDescription += `${key}: ${value}`;
    });
  }

  // Add URL separately if it exists (not as a credential)
  if (url) {
    if (fullDescription) fullDescription += '\n\n--- Additional Information ---\n';
    fullDescription += `URL: ${url}`;
  }

  // If no description but credentials, secrets, or URL exist, start with them
  if (!text && (username || password || secrets || url)) {
    fullDescription = fullDescription.trim();
  }

  if (fullDescription.length <= maxLength) {
    return (
      <div className="text-wrap">
        {fullDescription.split('\n').map((line, index) => (
          <div key={index} className={
            line.startsWith('Username:') || line.startsWith('Password:')
              ? 'text-danger font-weight-bold small bg-light p-1 rounded border-left border-danger'
              : line.startsWith('AES_') || line.startsWith('RSA_')
                ? 'font-weight-bold small bg-light p-1 rounded border-left border-warning'
                : line.startsWith('URL:')
                  ? 'text-info font-weight-bold small bg-light p-1 rounded border-left border-info'
                  : line.startsWith('---')
                    ? 'font-weight-bold text-center border-bottom '
                    : ''
          }>
            {line}
          </div>
        ))}
      </div>
    );
  }

  const displayText = isExpanded ? fullDescription : `${fullDescription.substring(0, maxLength)}...`;

  return (
    <div>
      <div className="text-wrap">
        {displayText.split('\n').map((line, index) => (
          <div key={index} className={
            line.startsWith('Username:') || line.startsWith('Password:')
              ? 'text-danger font-weight-bold small bg-light p-1 rounded border-left border-danger'
              : line.startsWith('AES_') || line.startsWith('RSA_')
                ? 'text-warning font-weight-bold small bg-light p-1 rounded border-left border-warning'
                : line.startsWith('URL:')
                  ? 'text-info font-weight-bold small bg-light p-1 rounded border-left border-info'
                  : line.startsWith('---')
                    ? 'text-muted font-weight-bold text-center border-bottom pb-1 mb-2'
                    : ''
          }>
            {line}
          </div>
        ))}
      </div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="btn btn-link btn-sm p-0 ml-1"
        style={{ textDecoration: 'none', color: '#007bff' }}
      >
        {isExpanded ? 'Show Less' : 'Show More'}
      </button>
    </div>
  );
};

// Screenshot component with modal view
const ScreenshotViewer = ({ screenshotPath, issueName }) => {
  const [showModal, setShowModal] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  if (!screenshotPath) return null;

  // Handle both full URLs and relative paths
  const fullImagePath = screenshotPath.startsWith('http') ? screenshotPath : `${API_URL}/uploads/${screenshotPath}`;

  return (
    <>
      <div className="text-center position-relative">
        {!imageLoaded && !imageError && (
          <div className="d-flex align-items-center justify-content-center" style={{ width: '80px', height: '60px' }}>
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}

        <img
          src={fullImagePath}
          alt={`Screenshot for ${issueName}`}
          className="img-thumbnail"
          style={{
            maxWidth: '80px',
            maxHeight: '60px',
            cursor: 'pointer',
            objectFit: 'cover',
            display: imageLoaded ? 'block' : 'none'
          }}
          onClick={() => setShowModal(true)}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          title={`Click to view full screenshot for: ${issueName}${screenshotPath.startsWith('http') ? `\nSource: ${screenshotPath.split('?')[0]}` : ''}`}
        />

        {/* External source indicator */}
        {screenshotPath.startsWith('http') && (
          <div
            className="position-absolute"
            style={{
              top: '-5px',
              right: '-5px',
              backgroundColor: '#17a2b8',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              border: '2px solid white'
            }}
            title="External source (S3)"
          >
            <i className="fa fa-cloud"></i>
          </div>
        )}

        {imageError && (
          <div
            className="text-muted small border rounded p-2"
            style={{
              width: '80px',
              height: '60px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={() => setShowModal(true)}
            title={`Click to view screenshot details${screenshotPath.startsWith('http') ? `\nSource: ${screenshotPath.split('?')[0]}` : ''}`}
          >
            <i className="fa fa-image fa-lg text-muted"></i>
            <small className="mt-1">View Details</small>
          </div>
        )}
      </div>

      {/* Modal for full-size screenshot */}
      {showModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          onClick={() => setShowModal(false)}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fa fa-image mr-2"></i>
                  Screenshot: {issueName}
                </h5>
                {screenshotPath.startsWith('http') && (
                  <small className="text-muted ml-2">
                    <i className="fa fa-link mr-1"></i>
                    External Source
                  </small>
                )}
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body text-center">
                <img
                  src={fullImagePath}
                  alt={`Screenshot for ${issueName}`}
                  className="img-fluid"
                  style={{ maxHeight: '70vh' }}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
                <a
                  href={fullImagePath}
                  download
                  className="btn btn-primary"
                >
                  <i className="fa fa-download mr-2"></i>
                  Download
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const RedTeamingIssue = () => {
  // State Management - organized by purpose
  // Data and loading states
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingReport, setDownloadingReport] = useState(false);

  // Pagination, filtering and sorting
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [filters, setFilters] = useState({});
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [category, setCategory] = useState('');
  const [sorting, setSorting] = useState({
    sortBy: 'updatedAt',
    sortOrder: 'desc'
  });

  // Selection and action states
  const [selectedIssueIds, setSelectedIssueIds] = useState([]);
  const [selectedIndividualIssueIds, setSelectedIndividualIssueIds] = useState([]);
  const [individualIssueIds, setIndividualIssueIds] = useState([]);
  const [lookalikeAppId, setLookalikeAppId] = useState([]);

  // Modal and form states
  const [isModal, setModal] = useState("");
  const [isModalTwo, setModalTwo] = useState("");
  const [action, setAction] = useState("");
  const [actionNote, setActionNote] = useState("");
  const [forValue, setForValue] = useState("forever");
  const [selectedValue, setSelectedValue] = useState("pleaseSelect");
  const [name, setName] = useState("");

  const { orgId } = useParams();
  const id = useSelector(state => {
    if (orgId) {
      return orgId;
    } else {
      return state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id;
    }
  });

  const auth = useSelector((state) => state?.auth?.user);
  const userPermission = useSelector(state => {
    return state?.auth?.user?.modulePermissionsConfig?.asm?.e;
  });

  const handleApiError = (error, defaultMessage = "An error occurred") => {
    // console.error removed
    let message = defaultMessage;

    if (error.response) {
      message = error.response.data?.message || error.response.data?.userMessage || message;
    } else if (error.message) {
      message = error.message;
    }

    toastr.error('Error', message);
  };

  const fetchIssues = async (page = 1, { ignoreDateRange = false } = {}) => {
    try {
      setLoading(true);
      if (!id) {
        toastr.error('Error', 'Organisation ID not found');
        return;
      }

      // Build params including optional date range
      // Remove any date keys that might have been echoed back into filters by the API
      const { startDate: _fStart, endDate: _fEnd, ...safeFilters } = filters || {};
      const params = {
        limit: pagination.limit,
        page: page,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder,
        ...safeFilters
      };

      params.category = category;

      if (!ignoreDateRange) {
        if (dateRange.startDate) params.startDate = dateRange.startDate;
        if (dateRange.endDate) params.endDate = dateRange.endDate;
      }

      const response = await axios.get(`${API_URL}/organisations/${id}/cart/issues`, { params });

      if (response.data?.success && response.data?.data) {
        setIssues(response.data.data.issues || []);
        setPagination(response.data.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          limit: 10,
          hasNextPage: false,
          hasPrevPage: false
        });
        // Clear selections when data changes
        setSelectedIssueIds([]);
        setSelectedIndividualIssueIds([]);
        // Sanitize filters to exclude date range so clear works properly
        if (response.data.data.filters) {
          const { startDate: _sd, endDate: _ed, ...restFilters } = response.data.data.filters;
          setFilters(restFilters);
        } else {
          setFilters({});
        }
        setSorting(response.data.data.sorting || {
          sortBy: 'updatedAt',
          sortOrder: 'desc'
        });
      }
    } catch (error) {
      handleApiError(error, "Failed to fetch issues");
      setIssues([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasNextPage: false,
        hasPrevPage: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    fetchIssues(page);
  };

  const downloadReport = async (selectedOnly = false) => {
    try {
      setDownloadingReport(true);

      if (!id) {
        toastr.error('Error', 'Organisation ID not found');
        return;
      }

      // Check if trying to download selected issues but none are selected
      if (selectedOnly && selectedIndividualIssueIds.length === 0) {
        toastr.warning('Warning', 'Please select at least one issue to download');
        return;
      }

      // Validate date range before requesting
      if (dateRange.startDate && dateRange.endDate && dateRange.startDate > dateRange.endDate) {
        toastr.error('Error', 'Start date cannot be after end date');
        return;
      }

      const reportParams = {};
      if (dateRange.startDate) reportParams.startDate = dateRange.startDate;
      if (dateRange.endDate) reportParams.endDate = dateRange.endDate;
      if (category) reportParams.category = category;

      // Add selected individual issue IDs if downloading selected only
      if (selectedOnly && selectedIndividualIssueIds.length > 0) {
        // For all selections, use a comma-separated string format
        reportParams.individualIssueId = selectedIndividualIssueIds.join(',');

        // Send selected IDs in the request params
      }

      const response = await axios.get(`${API_URL}/organisations/${id}/cart/issues/report`, {
        responseType: 'blob',
        params: reportParams
      });

      // Create blob link to download
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      const fileName = selectedOnly ? `selected-issues-report-${new Date().toISOString().slice(0, 10)}.pdf` : `all-issues-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
      toastr.success('Success', `${selectedOnly ? 'Selected' : 'All'} issues report downloaded successfully`);
    } catch (error) {
      handleApiError(error, "Failed to download issues report");
    } finally {
      setDownloadingReport(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchIssues(pagination.currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Optional: refetch when date range changes automatically (only when both dates valid)
  useEffect(() => {
    if (!id) return;
    if (dateRange.startDate && dateRange.endDate) {
      if (dateRange.startDate > dateRange.endDate) return; // invalid selection; user feedback occurs on apply
      fetchIssues(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate]);

  // Handle category filter changes
  useEffect(() => {
    if (!id) return;
    if (category !== undefined) {
      fetchIssues(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Function to select or deselect all issues
  const handleSelectAllIssues = (e) => {
    if (e.target.checked) {
      // Select all issues - primarily use individualIssueId
      const allIndividualIds = issues.map(issue => issue.individualIssueId);
      const allIssueIds = issues.map(issue => issue.issueId);
      setSelectedIndividualIssueIds(allIndividualIds);
      setSelectedIssueIds(allIssueIds); // Keep for backward compatibility
    } else {
      // Deselect all issues
      setSelectedIndividualIssueIds([]);
      setSelectedIssueIds([]);
    }
  };

  const getSeverityBadgeClass = (severity) => {
    const severityLower = severity.toLowerCase();
    switch (severityLower) {
      case 'critical':
        return 'badge-danger';
      case 'high':
        return 'badge-warning';
      case 'medium':
        return 'badge-info';
      case 'low':
        return 'badge-success';
      default:
        return 'badge-secondary';
    }
  };

  const getSeverityIcon = (severity) => {
    const severityLower = severity.toLowerCase();
    switch (severityLower) {
      case 'critical':
        return 'fa-exclamation-triangle';
      case 'high':
        return 'fa-exclamation-circle';
      case 'medium':
        return 'fa-info-circle';
      case 'low':
        return 'fa-check-circle';
      default:
        return 'fa-circle';
    }
  };


  // Function to handle issue actions (snooze/remediation) - simplified
  const snooze = (individualIssueId, event, name, uuid) => {
    // Check if this is a bulk operation (individualIssueId === "bulk" or selectedIndividualIssueIds has items)
    const isBulkOperation = individualIssueId === "bulk" || selectedIndividualIssueIds.length > 0;

    if (event === "markAsRemediated") {
      // Directly mark as remediated without showing modal
      let bulkIds;
      let individualIds;

      if (isBulkOperation) {
        // Use selectedIndividualIssueIds for bulk operations
        individualIds = selectedIndividualIssueIds;
        bulkIds = selectedIssueIds.length > 0 ? selectedIssueIds : selectedIndividualIssueIds;
      } else {
        // For individual issue operations
        bulkIds = individualIssueId ? [individualIssueId] : [];
        individualIds = uuid ? [uuid] : [];
      }

      submitAction(bulkIds, "markAsRemediated", "", "", individualIds);

      // Reset all related state
      setSelectedIndividualIssueIds([]);
      setSelectedIssueIds([]);
      setIndividualIssueIds([]);
      setSelectedValue("pleaseSelect");
    } else {
      // For other actions that require notes/duration, show the modal dialog
      setAction(event);
      setName(name);

      if (isBulkOperation) {
        // For bulk operations, don't set lookalikeAppId or individualIssueIds
        // They'll be set from selectedIssueIds in the submit function
      } else if (individualIssueId) {
        // For individual issue operations
        setLookalikeAppId([individualIssueId]);
        if (uuid) setIndividualIssueIds([uuid]);
      }

      setModalTwo("Form Wizard modal two");
    }
  };

  // Function to add modal - kept for backwards compatibility
  const addModal = () => setModalTwo("Form Wizard modal two");

  // Unified function to unsnooze issues - simplified like WebTechnology
  const unsnooze = (individualIssueId, uuid) => {
    // Check if this is a bulk operation (individualIssueId is empty or === "bulk" and selectedIndividualIssueIds has items)
    const isBulkOperation = (individualIssueId === "" || individualIssueId === "bulk") && selectedIndividualIssueIds.length > 0;

    let bulkIds;
    let individualIds;

    if (isBulkOperation) {
      // Use selectedIndividualIssueIds for bulk operations
      individualIds = selectedIndividualIssueIds;
      bulkIds = selectedIssueIds.length > 0 ? selectedIssueIds : selectedIndividualIssueIds;
    } else {
      // For individual operations
      bulkIds = individualIssueId ? [individualIssueId] : [];
      individualIds = uuid ? [uuid] : [];
    }

    submitAction(bulkIds, "resetAction", "", "", individualIds);
    setSelectedIndividualIssueIds([]);
    setSelectedIssueIds([]);
    setIndividualIssueIds([]);
  };

  // Alias for unsnooze for backwards compatibility
  const Unsnoozed = (individualIssueId, uuid) => unsnooze(individualIssueId, uuid);

  // Simplified function to submit the action for form submission - similar to WebTechnology
  const submit = () => {
    // Check if this is a bulk operation (selectedIndividualIssueIds has items and lookalikeAppId is empty)
    const isBulkOperation = selectedIndividualIssueIds.length > 0 && lookalikeAppId.length === 0;

    let bulkIds;
    let individualIds;

    if (isBulkOperation) {
      // Use selectedIndividualIssueIds for bulk operations
      individualIds = selectedIndividualIssueIds;
      // For bulk operations, use selectedIssueIds if available, otherwise fallback to selectedIndividualIssueIds
      bulkIds = selectedIssueIds.length > 0 ? selectedIssueIds : selectedIndividualIssueIds;
    } else {
      // For individual operations
      bulkIds = lookalikeAppId.length > 0 ? lookalikeAppId : [];
      individualIds = individualIssueIds.length > 0 ? individualIssueIds : lookalikeAppId;
    }

    submitAction(bulkIds, action, forValue, actionNote, individualIds);

    // Reset all state
    setModalTwo("");
    setActionNote("");
    setSelectedIndividualIssueIds([]);
    setSelectedIssueIds([]);
    setLookalikeAppId([]);
    setIndividualIssueIds([]);
    setSelectedValue("pleaseSelect");
  };

  // Function to submit the action (snooze or unsnooze) - simplified like WebTechnology
  const submitAction = async (individualIssueId, actionType, actionFor = "", note = "", individualIssueIds = []) => {
    try {
      setLoading(true);

      if (!id) {
        toastr.error('Error', 'Organisation ID not found');
        return;
      }

      // Validate individualIssueId to ensure it's never ["bulk"]
      let normalizedIssueIds = individualIssueId;
      if (Array.isArray(individualIssueId) && individualIssueId.length === 1 && individualIssueId[0] === "bulk") {
        // Replace "bulk" with actual selectedIssueIds
        normalizedIssueIds = selectedIssueIds;
      }

      // Validate individualIssueIds to ensure it's never ["bulk"]
      let normalizedIndividualIssueIds = individualIssueIds;
      if (Array.isArray(individualIssueIds) && individualIssueIds.length === 1 && individualIssueIds[0] === "bulk") {
        // For bulk operations, populate with selectedIssueIds
        normalizedIndividualIssueIds = selectedIssueIds;
      }

      // If individualIssueIds is empty but individualIssueId has values, use individualIssueId for both
      if ((!normalizedIndividualIssueIds || normalizedIndividualIssueIds.length === 0) &&
        normalizedIssueIds && normalizedIssueIds.length > 0) {
        normalizedIndividualIssueIds = [...normalizedIssueIds];
      }

      // Check if at least individualIssueId has items
      if (!normalizedIssueIds || normalizedIssueIds.length === 0) {
        toastr.error('Error', 'No issues selected');
        return;
      }

      // Prepare request payload with normalized arrays
      const payload = {
        action: actionType,
        actionFor: actionFor,
        actionNote: note,
        issueIds: normalizedIssueIds,
        individualIssueIds: normalizedIndividualIssueIds
      };

      // Make the API call
      const response = await axios.post(`${API_URL}/organisations/${id}/cart/issues/action`, payload);

      if (response.data?.success) {
        const actionMessages = {
          'snooze': 'snoozed',
          'resetAction': 'unsnoozed',
          'false-positive': 'marked as false positive',
          'risk-accepted': 'marked as risk accepted',
          'markAsRemediated': 'marked as remediated',
          'remediated': 'marked as remediated'
        };

        const message = actionMessages[actionType] || 'updated';
        const totalIssues = (payload.issueIds?.length || 0) + (payload.individualIssueIds?.length || 0);
        const pluralSuffix = totalIssues > 1 ? 's' : '';

        toastr.success('Success', `Issue${pluralSuffix} ${message} successfully`);

        // Reset states
        setModal("");
        setActionNote("");
        setForValue("forever");

        // Clear selection for bulk actions
        if (isModal === "bulk") {
          setSelectedIssueIds([]);
          setSelectedIndividualIssueIds([]);
        }

        fetchIssues(pagination.currentPage);
      } else {
        toastr.error('Error', response.data?.message || 'Failed to update issue status');
      }
    } catch (error) {
      handleApiError(error, `Failed to ${actionType === 'snooze' ? 'snooze' : 'process'} issue`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="block-header">
        <div className="row clearfix">
          <div className="col-md-6 col-sm-12">
            <h1 className="welcome-text">Security Issues</h1>
            <nav aria-label="breadcrumb" className="mt-1">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/" className="welcome-textSmall">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/red-teaming/dashboard">Red Teaming Dashboard</Link>
                </li>
                <li
                  className="breadcrumb-item active"
                  aria-current="page"
                  style={{ color: "white" }}
                >
                  Security Issues
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      {/* Removed old Snooze Modal in favor of WebTechnology style modal */}

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <ul className="nav nav-tabs mb-2" id="myTabs" role="tablist">
                <li className="nav-item">
                  <a
                    className={`nav-table-tab nav-link ${category === "" ? "active" : ""
                      }`}
                    onClick={() => {
                      setCategory("");
                      setPagination(prev => ({ ...prev, currentPage: 1 }));
                      setSelectedIndividualIssueIds([]);
                      setSelectedIssueIds([]);
                      setAction("");
                    }}
                  >
                    Current
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link nav-table-tab ${category === "risk-accepted" ? "active" : ""
                      }`}
                    onClick={() => {
                      setCategory("risk-accepted");
                      setPagination(prev => ({ ...prev, currentPage: 1 }));
                      setSelectedIndividualIssueIds([]);
                      setSelectedIssueIds([]);
                      setAction("");
                    }}
                  >
                    Risk Accepted
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link nav-table-tab ${category === "false-positive" ? "active" : ""
                      }`}
                    onClick={() => {
                      setCategory("false-positive");
                      setPagination(prev => ({ ...prev, currentPage: 1 }));
                      setSelectedIndividualIssueIds([]);
                      setSelectedIssueIds([]);
                      setAction("");
                    }}
                  >
                    False Positive
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link nav-table-tab ${category === "remediated" ? "active" : ""
                      }`}
                    onClick={() => {
                      setCategory("remediated");
                      setPagination(prev => ({ ...prev, currentPage: 1 }));
                      setSelectedIndividualIssueIds([]);
                      setSelectedIssueIds([]);
                      setAction("");
                    }}
                  >
                    Remediated
                  </a>
                </li>
              </ul>
              <div className="table-responsive">
                {selectedIndividualIssueIds.length > 0 && (
                  <div className="alert alert-info d-flex justify-content-between align-items-center py-2 px-3 mb-2">
                    <span>
                      <i className="fa fa-check-square mr-2"></i>
                      <strong>{selectedIndividualIssueIds.length}</strong> issues selected
                    </span>
                    <div className="d-flex align-items-center">
                      {selectedIndividualIssueIds?.length > 1 && userPermission && category === "" && (
                        <Box sx={{ width: 160, marginRight: 2 }}>
                          <FormControl fullWidth>
                            <Select
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              value={selectedValue}
                              onChange={(event) => {
                                setAction(event.target.value);
                                // For bulk actions, we pass "bulk" to indicate it's a bulk operation
                                // The snooze function will use selectedIssueIds
                                snooze("bulk", event.target.value, "", "");
                              }}
                            >
                              <MenuItem value="pleaseSelect">
                                Snooze Options
                              </MenuItem>
                              <MenuItem
                                value="riskAccepted"
                                onClick={() => {
                                  // Use snooze with bulk flag to handle selected issues
                                  snooze("bulk", "riskAccepted", "", "");
                                }}
                              >
                                Risk Accepted
                              </MenuItem>
                              <MenuItem
                                value="markAsFalsePositive"
                                onClick={() => {
                                  // Use snooze with bulk flag to handle selected issues
                                  snooze("bulk", "markAsFalsePositive", "", "");
                                }}
                              >
                                False Positive
                              </MenuItem>
                              <MenuItem value="markAsRemediated">
                                Remediated
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      )}
                      {selectedIndividualIssueIds?.length > 1 &&
                        (category === "false-positive" ||
                          category === "risk-accepted") && userPermission && (
                          <button
                            className="btn btn-primary btn-sm mr-2 Unsnooze"
                            onClick={() => unsnooze("", "")}
                          >
                            Unsnooze
                          </button>
                        )}
                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={() => {
                          setSelectedIndividualIssueIds([]);
                          setSelectedIssueIds([]);
                        }}
                      >
                        <i className="fa fa-times-circle mr-1"></i>
                        Clear Selection
                      </button>
                    </div>
                  </div>
                )}
                <div className="d-flex flex-wrap justify-content-between mb-3 align-items-end">
                  <div className="d-flex flex-wrap align-items-end mb-2 mb-sm-0" style={{ gap: '0.75rem' }}>
                    <div>
                      <label className="small text-muted mb-1 d-block">Start Date</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={dateRange.startDate}
                        max={dateRange.endDate || undefined}
                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="small text-muted mb-1 d-block">End Date</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={dateRange.endDate}
                        min={dateRange.startDate || undefined}
                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                      />
                    </div>
                    <div className="d-flex" style={{ gap: '0.5rem' }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary mt-3"
                        onClick={() => {
                          if (dateRange.startDate && dateRange.endDate && dateRange.startDate > dateRange.endDate) {
                            toastr.error('Error', 'Start date cannot be after end date');
                            return;
                          }
                          fetchIssues(1);
                        }}
                        disabled={loading}
                      >
                        <i className="fa fa-filter mr-1"></i> Apply
                      </button>
                      {(dateRange.startDate || dateRange.endDate || category) && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary mt-3"
                          onClick={() => {
                            setDateRange({ startDate: '', endDate: '' });
                            setCategory('');
                            setPagination(prev => ({ ...prev, currentPage: 1 }));
                            setSelectedIndividualIssueIds([]); // Clear selections when filters are cleared
                            setSelectedIssueIds([]); // Also clear issue IDs for compatibility
                            // Defer fetch so state updates apply; fetch without date params
                            setTimeout(() => fetchIssues(1, { ignoreDateRange: true }), 0);
                          }}
                          disabled={loading}
                        >
                          <i className="fa fa-times mr-1"></i> Clear
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="btn-group">
                    <button
                      className="btn btn-primary"
                      onClick={() => downloadReport(false)}
                      disabled={downloadingReport}
                    >
                      {downloadingReport ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                          Downloading...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-download mr-2"></i>
                          Download All Issues
                        </>
                      )}
                    </button>
                    <button
                      className="btn btn-primary ml-2"
                      onClick={() => downloadReport(true)}
                      disabled={downloadingReport || selectedIndividualIssueIds.length === 0}
                      title={selectedIndividualIssueIds.length === 0 ? "Select issues first" : `Download ${selectedIndividualIssueIds.length} selected issues`}
                    >
                      <i className="fa fa-file-export mr-2"></i>
                      Download Selected ({selectedIndividualIssueIds.length})
                    </button>
                  </div>
                </div>
                <table className="table table-hover table-custom spacing8" style={{ marginBottom: 0 }}>
                  <thead>
                    <tr className="bg-light">
                      <th style={{ width: '3%', textAlign: 'center', verticalAlign: 'middle' }}>
                        <input
                          type="checkbox"
                          id="checkAllIssues"
                          checked={issues.length > 0 && selectedIndividualIssueIds.length === issues.length}
                          onChange={handleSelectAllIssues}
                          style={{ cursor: 'pointer' }}
                        />
                      </th>
                      <th style={{ width: '12%', verticalAlign: 'middle', fontWeight: 'bold' }}>Issue Name</th>
                      <th style={{ width: '18%', verticalAlign: 'middle' }}>Description</th>
                      <th style={{ width: '8%', textAlign: 'center', verticalAlign: 'middle' }}>Severity</th>
                      <th style={{ width: '18%', verticalAlign: 'middle' }}>Recommendation</th>
                      <th style={{ width: '8%', verticalAlign: 'middle' }}>Target Type</th>
                      <th style={{ width: '12%', verticalAlign: 'middle' }}>Simulation Name</th>
                      <th style={{ width: '8%', textAlign: 'center', verticalAlign: 'middle' }}>Screenshot</th>
                      <th style={{ width: '5%', textAlign: 'center', verticalAlign: 'middle' }}>Created At</th>
                      <th style={{ width: '5%', textAlign: 'center', verticalAlign: 'middle' }}>Updated At</th>
                      <th style={{ width: '8%', textAlign: 'center', verticalAlign: 'middle' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="12" className="text-center py-5">
                          <div className="d-flex flex-column align-items-center">
                            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                              <span className="sr-only">Loading...</span>
                            </div>
                            <p className="text-muted mb-0">Loading security issues...</p>
                          </div>
                        </td>
                      </tr>
                    ) : issues?.length > 0 ? (
                      issues?.map((issue, index) => (
                        <tr key={issue.issueId} className="border-bottom" onClick={(e) => e.stopPropagation()}>
                          <td className="text-center" style={{ verticalAlign: 'middle' }}>
                            <input
                              type="checkbox"
                              id={`check-${issue.individualIssueId}`}
                              onChange={(e) => {
                                // Only modify this specific individual issue ID
                                if (e.target.checked) {
                                  // Add the individual issue ID as primary
                                  setSelectedIndividualIssueIds(prev => [...prev, issue.individualIssueId]);
                                  // Also add the issue ID for backward compatibility
                                  setSelectedIssueIds(prev => [...prev, issue.issueId]);
                                } else {
                                  // Remove the individual issue ID
                                  setSelectedIndividualIssueIds(prev => prev.filter(id => id !== issue.individualIssueId));
                                  // Also remove the issue ID for backward compatibility
                                  setSelectedIssueIds(prev => prev.filter(id => id !== issue.issueId));
                                }
                              }}
                              checked={selectedIndividualIssueIds.includes(issue.individualIssueId)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>
                            <div className="font-weight-semibold text-dark">
                              <a href="#" className="text-primary" title={issue?.issueName} onClick={(e) => {
                                e.preventDefault();
                                // Toggle selection for this specific issue when clicking on name
                                if (selectedIndividualIssueIds.includes(issue.individualIssueId)) {
                                  setSelectedIndividualIssueIds(prev => prev.filter(id => id !== issue.individualIssueId));
                                  setSelectedIssueIds(prev => prev.filter(id => id !== issue.issueId));
                                } else {
                                  setSelectedIndividualIssueIds(prev => [...prev, issue.individualIssueId]);
                                  setSelectedIssueIds(prev => [...prev, issue.issueId]);
                                }
                              }}>
                                {issue?.issueName}
                              </a>
                            </div>
                          </td>
                          <td style={{ verticalAlign: 'middle', maxWidth: '400px' }}>
                            <ExpandableDescription
                              text={issue?.issueDescription}
                              username={issue?.username}
                              password={issue?.password}
                              url={issue?.url}
                              secrets={issue?.secrets}
                              maxLength={150}
                            />
                          </td>
                          <td className="text-center" style={{ verticalAlign: 'middle' }}>
                            <span className={`badge ${getSeverityBadgeClass(issue?.issueSeverity)} badge-pill`}>
                              <i className={`fa ${getSeverityIcon(issue?.issueSeverity)} mr-1`}></i>
                              {issue?.issueSeverity?.toUpperCase()}
                            </span>
                          </td>
                          <td style={{ verticalAlign: 'middle', maxWidth: '400px' }}>
                            <ExpandableDescription text={issue.recommendation} maxLength={150} />
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>
                            <span className="badge badge-outline-secondary">
                              {issue?.targetType}
                            </span>
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>
                            <span className="badge badge-info badge-pill">
                              <i className="fa fa-play mr-1"></i>
                              {issue?.stimulationName}
                            </span>
                          </td>

                          <td className="text-center" style={{ verticalAlign: 'middle' }}>
                            <ScreenshotViewer
                              screenshotPath={issue?.screenshotPath}
                              issueName={issue?.issueName}
                            />
                          </td>

                          <td className="text-center" style={{ verticalAlign: 'middle' }}>
                            <div className="text-muted">
                              <div className="font-weight-semibold">
                                {new Date(issue?.createdAt).toLocaleDateString()}
                              </div>
                              <small className="text-muted">
                                {new Date(issue?.createdAt).toLocaleTimeString()}
                              </small>
                            </div>
                          </td>
                          <td className="text-center" style={{ verticalAlign: 'middle' }}>
                            <div className="text-muted">
                              <div className="font-weight-semibold">
                                {new Date(issue?.updatedAt).toLocaleDateString()}
                              </div>
                              <small className="text-muted">
                                {new Date(issue?.updatedAt).toLocaleTimeString()}
                              </small>
                            </div>
                          </td>
                          <td className="text-center" style={{ verticalAlign: 'middle', width: '7%' }}>
                            {issue?.isRemediationPending == true ?
                              <span>Retest in progress</span>
                              :
                              <>
                                {auth?.accessLevel != "Threat Analyst" && userPermission && category === "" && (
                                  <Box sx={{ width: 160 }}>
                                    <FormControl fullWidth>
                                      <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        value={selectedValue}
                                        onChange={(event) => {
                                          setAction(event.target.value);
                                          // Don't call snooze here, as it's handled by the MenuItem onClick
                                          if (event.target.value === "markAsRemediated") {
                                            submitAction(issue?.issueId, "markAsRemediated", "", "", issue?.individualIssueId);
                                          }
                                        }}
                                      >
                                        <MenuItem value="pleaseSelect">
                                          Snooze Options
                                        </MenuItem>
                                        <MenuItem
                                          value="riskAccepted"
                                          onClick={() => snooze(issue?.issueId, "riskAccepted", issue?.issueName, issue?.individualIssueId)}
                                        >
                                          Risk Accepted
                                        </MenuItem>
                                        <MenuItem
                                          value="markAsFalsePositive"
                                          onClick={() => snooze(issue?.issueId, "markAsFalsePositive", issue?.issueName, issue?.individualIssueId)}
                                        >
                                          False Positive
                                        </MenuItem>
                                        <MenuItem
                                          value="markAsRemediated"
                                          onClick={() => snooze(issue?.issueId, "markAsRemediated", issue?.issueName, issue?.individualIssueId)}
                                        >
                                          Remediated
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Box>
                                )}
                                {auth?.accessLevel != "Threat Analyst" && (category === "risk-accepted" ||
                                  category === "false-positive") && (
                                    <button
                                      className="btn btn-primary btn-sm mr-1 Unsnooze mb-2"
                                      onClick={() =>
                                        Unsnoozed(
                                          issue?.issueId,
                                          issue?.individualIssueId
                                        )
                                      }
                                    >
                                      Unsnooze
                                    </button>
                                  )}
                              </>
                            }
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="12" className="text-center py-5">
                          <div className="d-flex flex-column align-items-center">
                            <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
                            <h5 className="text-muted mb-2">No Issues Found</h5>
                            <p className="text-muted mb-0">There are currently no security issues to display.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination?.totalCount > 0 && (
                <div className="row mt-4">
                  <div className="col-md-6">
                    <div className="text-muted">
                      Showing {((pagination?.currentPage - 1) * pagination?.limit) + 1} to {Math.min(pagination?.currentPage * pagination?.limit, pagination?.totalCount)} of {pagination?.totalCount} issues
                    </div>
                  </div>
                  <div className="col-md-6 d-flex justify-content-end">
                    <div className="dataTables_paginate">
                      <Pagination
                        className="pagination-bar"
                        currentPage={pagination?.currentPage}
                        totalCount={pagination?.totalCount}
                        pageSize={pagination?.limit}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* WebTechnology style modal for snooze/false positive options */}
      <div
        className={`modal fade Form-Wizard-modal ${isModalTwo === "Form Wizard modal two" ? "d-block show" : ""}`}
      >
        <div className="modal-dialog modal-lg" id="clientModal">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                {action === "markAsFalsePositive"
                  ? `Mark As False Positive`
                  : `Risk Accepted`}
              </h5>
              <button
                type="button"
                data-dismiss="modal"
                aria-label="Close"
                onClick={() => {
                  setModalTwo("");
                  setActionNote("");
                }}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body pricing_page modal_mob">
              <div className="col-xl-12 col-lg-12 col-md-12">
                <div className="card">
                  <div className="row clearfix">
                    <div className="col-md-12">
                      <label className="col-lg-6 col-md-12">Snooze for</label>
                      <div className="form-group ol-lg-12 col-md-12">
                        <select
                          className="custom-select custom-select-sm form-control form-control-sm"
                          onChange={(e) => setForValue(e.target.value)}
                          value={forValue}
                        >
                          <option value="forever">Forever</option>
                          <option value="3 months">3 Months</option>
                          <option value="6 months">6 Months</option>
                          <option value="12 months">12 Months</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <label className="col-lg-12 col-md-12">Detail</label>
                      <div className="form-group col-lg-12 col-md-12">
                        <textarea
                          onChange={(e) => setActionNote(e.target.value)}
                          value={actionNote}
                          className="form-control"
                          rows="3"
                        ></textarea>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div
                      className="col-lg-12 col-md-12"
                      style={{ marginRight: "28px" }}
                    >
                      <button
                        type="button"
                        style={{ color: "black", fontWeight: "bold" }}
                        onClick={submit}
                        className="btn btn-round btn-primary mr-1 pull-right"
                      >
                        Submit
                      </button>
                      &nbsp;&nbsp;
                      <button
                        type="button"
                        onClick={() => {
                          setModalTwo("");
                          setActionNote("");
                          setSelectedValue("pleaseSelect");
                          setLookalikeAppId([]);
                          setIndividualIssueIds([]);
                          setName("");
                        }}
                        className="btn btn-round btn-default pull-right"
                        style={{ marginRight: "10px" }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedTeamingIssue;
